import { getListCompetitorPostsQueryKey } from '@reelplicate/api-client/competitors';
import {
  getListMediaQueryKey,
  getMediaImport,
  useCreateMediaImport,
} from '@reelplicate/api-client/media';
import { getListMediaCollectionsQueryKey } from '@reelplicate/api-client/media-collections';
import type { MediaImportDto } from '@reelplicate/api-client/model';
import { useQueryClient } from '@tanstack/react-query';

import { ENTRIES_KEY } from './use-collection-entries';

/** How often to ask whether the post has been fetched. */
const POLL_MS = 2000;

const running = (status: MediaImportDto['status']) =>
  status === 'QUEUED' || status === 'RUNNING';

/**
 * Saves a TikTok or Instagram post to the library from its link, waiting
 * for the server to fetch it: resolves with the finished import - DONE
 * (`asset` set) or FAILED (`error` says why) - or null if `stop()` said to
 * stop waiting. Errors starting it (a bad link, the daily limit) throw; the
 * caller shows them, so they aren't toasted.
 */
export function useSavePost(workspaceId: string) {
  const queryClient = useQueryClient();
  const create = useCreateMediaImport({ mutation: { meta: { silent: true } } });

  const save = async (
    url: string,
    { collectionId, stop }: { collectionId?: string; stop?: () => boolean } = {},
  ): Promise<MediaImportDto | null> => {
    let progress = await create.mutateAsync({
      id: workspaceId,
      data: { url, ...(collectionId ? { collectionId } : {}) },
    });
    while (running(progress.status)) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      if (stop?.()) return null;
      progress = await getMediaImport(workspaceId, progress.id);
    }
    if (progress.status === 'DONE') {
      await Promise.all(
        [
          getListMediaQueryKey(workspaceId),
          getListMediaCollectionsQueryKey(workspaceId),
          getListCompetitorPostsQueryKey(workspaceId),
          // A saved post moves from a smart collection's feed to its assets.
          [ENTRIES_KEY, workspaceId],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    }
    return progress;
  };

  return { save };
}
